import React, { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Form,
  FloatingLabel,
  Card,
  Button,
  Badge,
  Spinner,
  Modal,
} from "react-bootstrap";
import { FileText, FileMinus, Edit3, Download, Truck } from "react-feather";

import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";

import { useAuth } from "../../contexts/authContext";
import { useConfirmation } from "../../contexts/confirmationContext";
import apiService from "../../services/apiService";
import { formatAmount, formatTime } from "../../utils";

const Orders = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();

  const [orders, setOrders] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 15 });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loadingState, setLoadingState] = useState({ action: "", id: "" });

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  // Fetch orders on page load or when pagination/filter changes
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const apiURL =
          user.role === "super_admin" || user.role === "administrator"
            ? `/orders/all`
            : `/orders/user`;

        const response = await apiService.get(apiURL, {
          page: page.PageNumber,
          limit: page.PageSize,
          status: selectedStatus,
        });

        setOrders(response.orders); // Update orders data
        setTotalRecords(response.pagination.total); // Update total records
      } catch (error) {
        showNotification("danger", error || "Failed to get orders.", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [
    user.role,
    page.PageNumber,
    page.PageSize,
    selectedStatus,
    showNotification,
  ]);

  // Handle page change
  const handlePageChange = (pageNumber, pageSize) => {
    setPage({ PageNumber: pageNumber, PageSize: pageSize });
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage({ PageNumber: 1, PageSize: page.PageSize }); // Reset to the first page
  };

  // Define columns
  const columns = [
    {
      accessorKey: "customerName",
      header: "Customer Name",
      cell: (info) => (
        <Fragment>
          {info.row.original.User?.firstName} {info.row.original.User?.lastName}
        </Fragment>
      ),
    },
    { accessorKey: "orderNumber", header: "Order Number" },
    {
      accessorKey: "paymentStatus",
      header: "Payment Status",
      cell: (info) => (
        <Badge
          className="text-uppercase"
          bg={
            info.getValue() === "pending"
              ? "warning"
              : info.getValue() === "failed"
              ? "danger"
              : info.getValue() === "refunded"
              ? "primary"
              : info.getValue() === "paid"
              ? "success"
              : "danger"
          }
        >
          {info.getValue()}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => (
        <Badge
          className="text-uppercase"
          bg={
            info.getValue() === "pending" ||
            info.getValue() === "cancel_request"
              ? "warning"
              : info.getValue() === "processing"
              ? "info"
              : info.getValue() === "confirmed"
              ? "primary"
              : info.getValue() === "shipped"
              ? "secondary"
              : info.getValue() === "delivered"
              ? "success"
              : "danger"
          }
        >
          {info.getValue()}
        </Badge>
      ),
    },
    { accessorKey: "shippingDetails.order_id", header: "ShipRocket OrderId" },
    {
      accessorKey: "total",
      header: "Amount",
      cell: (info) => (
        <Fragment>
          <span>
            <span className="currency">&#8377;</span>
            {formatAmount(info.getValue() || 0)}
          </span>
        </Fragment>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Ordered On",
      cell: (info) => <Fragment>{formatTime(info.getValue())}</Fragment>,
    },

    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div>
          <Link
            title="View Order"
            to={`/orders/view/${info.row.original.id}`}
            className="btn btn-primary btn-sm me-1 mb-1"
          >
            <FileText size={16} />
          </Link>
          {user.role === "super_admin" &&
            info.row.original.paymentStatus === "paid" && (
              <Button
                variant="secondary"
                size="sm"
                title="Update Status"
                onClick={() => triggerModal(info.row.original.id)}
                className="me-1 mb-1"
              >
                <Edit3 size={16} />
              </Button>
            )}
          {info.row.original.paymentStatus === "paid" && (
            <Button
              variant="dark"
              size="sm"
              title="Download Invoice"
              onClick={() => downloadInvoice(info.row.original.id)}
              className="me-1 mb-1"
              disabled={
                loadingState?.action === "invoice" &&
                loadingState.id === info.row.original.id
              }
            >
              {loadingState?.action === "invoice" &&
              loadingState.id === info.row.original.id ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                <Download size={16} />
              )}
            </Button>
          )}
          {user.role === "super_admin" &&
            info.row.original.status !== "cancelled" && (
              <Button
                variant="danger"
                size="sm"
                title="Cancel Order"
                onClick={() =>
                  handleCancelOrder(
                    info.row.original.id,
                    info.row.original.orderNumber
                  )
                }
                className="me-1 mb-1"
                disabled={
                  loadingState?.action === "cancel" &&
                  loadingState.id === info.row.original.id
                }
              >
                {loadingState?.action === "cancel" &&
                loadingState.id === info.row.original.id ? (
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : (
                  <FileMinus size={16} />
                )}
              </Button>
            )}

          {user.role !== "super_admin" &&
            info.row.original.status !== "cancelled" &&
            info.row.original.status !== "cancel_request" && ( // Optional: prevent duplicate request
              <Button
                variant="warning"
                size="sm"
                title="Request Cancellation"
                onClick={() =>
                  handleRequestToCancelOrder(
                    info.row.original.id,
                    info.row.original.orderNumber
                  )
                }
                className="me-1 mb-1"
                disabled={
                  loadingState?.action === "cancel_request" &&
                  loadingState.id === info.row.original.id
                }
              >
                {loadingState?.action === "cancel_request" &&
                loadingState.id === info.row.original.id ? (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">Sending...</span>
                  </Spinner>
                ) : (
                  <>
                    <FileMinus size={16} className="me-1" />
                  </>
                )}
              </Button>
            )}

          {user.role === "super_admin" &&
            info.row.original.status !== "cancelled" &&
            info.row.original.paymentStatus === "paid" &&
            !info.row.original.shippingDetails?.order_id && (
              <Button
                variant="secondary"
                size="sm"
                title="Create Order in ShipRocket"
                onClick={() =>
                  handleCreateOrderInShipRocket(info.row.original.id)
                }
                className="me-1 mb-1"
                disabled={
                  loadingState?.action === "create ship-rocket order" &&
                  loadingState.id === info.row.original.id
                }
              >
                {loadingState?.action === "create ship-rocket order" &&
                loadingState.id === info.row.original.id ? (
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : (
                  <Truck size={16} />
                )}
              </Button>
            )}
        </div>
      ),
    },
  ];

  // Handle Cancel Order
  const handleCancelOrder = async (orderId, refId) => {
    showConfirmation({
      title: "Cancel Order",
      description: `Cancelling order #${refId} cannot be undone.`,
      confirmText: "Yes, Cancel",
      cancelText: "No, Keep It",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "cancel", id: orderId });
        try {
          await apiService.post(`/orders/${orderId}/cancel`);
          // Update the order status in the local state
          setOrders((prev) =>
            prev.map((order) =>
              order.id === orderId ? { ...order, status: "cancelled" } : order
            )
          );
          showNotification("success", "Order cancelled successfully!");
        } catch (error) {
          showNotification(
            "danger",
            error?.message || "Failed to cancel order."
          );
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  const handleRequestToCancelOrder = async (orderId, refId) => {
    let reasonInput = "";

    showConfirmation({
      title: "Request Cancellation",
      description: (
        <div>
          <p>
            Are you sure you want to request cancellation for order{" "}
            <strong>#{refId}</strong>? A super admin will review your request.
          </p>
          <label htmlFor="cancel-reason" className="form-label mt-3">
            Reason for cancellation
          </label>
          <textarea
            id="cancel-reason"
            className="form-control"
            rows="3"
            onChange={(e) => {
              reasonInput = e.target.value;
            }}
            placeholder="Enter your reason here..."
          ></textarea>
        </div>
      ),
      confirmText: "Yes, Send Request",
      cancelText: "No, Keep It",
      variant: "primary",
      onConfirm: async () => {
        if (!reasonInput.trim()) {
          showNotification(
            "danger",
            "Please provide a reason for cancellation."
          );
          return;
        }

        setLoadingState({ action: "cancel_request", id: orderId });

        try {
          await apiService.post(`/orders-cancel-request/cancel-request`, {
            orderId,
            reason: reasonInput.trim(),
          });

          setOrders((prev) =>
            prev.map((order) =>
              order.id === orderId
                ? { ...order, status: "cancel_request" }
                : order
            )
          );

          showNotification(
            "success",
            "Cancellation request sent successfully!"
          );
        } catch (error) {
          showNotification(
            "danger",
            typeof error === "string"
              ? error
              : error?.message || "Failed to send cancellation request."
          );
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  const handleCreateOrderInShipRocket = async (orderId) => {
    setLoadingState({ action: "create ship-rocket order", id: orderId });
    try {
      const response = await apiService.post(`/shipping/orders/${orderId}`);

      if (response.status === "NEW") {
        setOrders((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? { ...order, shippingDetails: response }
              : order
          )
        );
        showNotification(
          "success",
          "Order created in ShipRocket successfully!"
        );
      } else if (response.error) {
        showNotification(
          "danger",
          response.error || "Failed to create order in ShipRocket."
        );
      }
    } catch (error) {
      showNotification(
        "danger",
        error || "Failed to create order in ShipRocket."
      );
    } finally {
      setLoadingState(null);
    }
  };

  const triggerModal = (orderId) => {
    setSelectedOrder(orderId);
    setShowModal(true);
  };

  // Handle Update Status
  const handleUpdateStatus = async () => {
    if (!newStatus) {
      showNotification("danger", "Please select a status to update.", 3000);
      return;
    }

    setLoadingState({ action: "update", id: selectedOrder });
    try {
      await apiService.put(`/orders/${selectedOrder}/status`, {
        status: newStatus,
      });

      // Update the order status in the local state
      setOrders((prev) =>
        prev.map((order) =>
          order.id === selectedOrder ? { ...order, status: newStatus } : order
        )
      );

      showNotification("success", "Order status updated successfully!");
      setShowModal(false);
      setNewStatus(""); // Reset to default value
    } catch (error) {
      showNotification(
        "danger",
        error || "Failed to update status of the order."
      );
    } finally {
      setLoadingState(null);
    }
  };

  const downloadInvoice = async (orderId) => {
    setLoadingState({ action: "invoice", id: orderId });
    try {
      // Fetch the invoice as a Blob
      const response = await apiService.download(`/invoices/${orderId}`, {
        responseType: "blob", // Ensure the response is a Blob
      });

      // Check if the response is a Blob
      if (!(response instanceof Blob)) {
        throw new Error("Invalid request.");
      }

      // Create a Blob URL for the file
      const blobUrl = URL.createObjectURL(response);

      // Extract file name from headers
      let fileName = `invoice-${orderId}.pdf`;

      const contentDisposition = response.headers?.["content-disposition"];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // Create a hidden anchor element
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName; // Set the file name
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      // Show success notification
      showNotification("success", "Invoice downloaded successfully!");
    } catch (error) {
      showNotification("danger", error || "Failed to download invoice.");
    } finally {
      setLoadingState(null);
    }
  };

  return (
    <Card className="p-4 rounded-4">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mb-2 fs-5">Manage Orders</h1>
            <p className="mb-0 text-light-emphasis fw-light">
              View order details, track order status, etc.
            </p>
          </div>
          <FloatingLabel controlId="ddStatus" label="Filter by Status">
            <Form.Select
              aria-label="Filter by Status"
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{ width: "150px" }}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="confirmed">Confirmed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </FloatingLabel>
        </div>
        <hr className="my-4" />
        <DataTable
          columns={columns}
          data={orders}
          totalRecords={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          loadingState={isLoading}
          searchPlaceholder="Search orders..."
        />
      </Card.Body>
      <Modal
        centered
        show={showModal}
        onHide={() => setShowModal(false)}
        contentClassName="p-4 border-0"
      >
        <Modal.Body>
          <h5>Update Order Status</h5>
          <p className="mb-4 text-muted">
            Select the new status for the order. This action will update the
            order's current status and notify the customer if applicable.
          </p>
          <FloatingLabel controlId="ddNewStatus" label="Select New Status">
            <Form.Select
              aria-label="Select New Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              required
            >
              <option value="">Select a status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="confirmed">Confirmed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
            {!newStatus && (
              <Form.Text className="text-danger">
                Please select a status to proceed.
              </Form.Text>
            )}
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowModal(false);
              setNewStatus(""); // Reset to default value
            }}
            className="btn-main"
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateStatus}
            disabled={loadingState?.action === "update" || !newStatus} // Disable if no status is selected
            className="btn-main"
          >
            {loadingState?.action === "update" ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "Update Status"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Orders;
